import { signOut } from 'firebase/auth'
import React, { useCallback } from 'react'
import { toast } from 'react-toastify'

import { auth } from 'providers/firebaseAuth'
import { useImpersonationSession } from 'providers/ImpersonationProvider'

/**
 * Full-width impersonation banner shown at the top of every CRM page while an
 * impersonation session is active. State comes from ImpersonationProvider — no
 * localStorage reads here.
 *
 * "End session" notifies the TM workspace tab via postMessage then closes this tab.
 */
export default function ImpersonationBanner() {
  const { session, clearSession } = useImpersonationSession()

  const handleEndSession = useCallback(() => {
    // Ending the session server-side happens in Tenant Manager, which holds the internal SSO
    // session — this CRM tab can't call that API directly. We ask the TM tab to do it via
    // postMessage. If we can't reach it (opener closed/navigated), the backend session can't be
    // ended from here and will linger until its TTL, so don't silently close as if it ended.
    let notifiedTm = false
    if (session?.tmOrigin && window.opener) {
      try {
        window.opener.postMessage({ type: 'REQUEST_END_SESSION', sessionId: session.sessionId }, session.tmOrigin)
        notifiedTm = true
      } catch {
        /* opener navigated away — treat as unable to notify */
      }
    }
    if (!notifiedTm) {
      // Can't reach the TM tab to end the session server-side; it will expire on its own. Warn the
      // operator rather than implying a clean end, but still drop this tab's impersonation locally.
      toast.warning(
        "Couldn't reach Tenant Manager to end this session on the server — it will expire " +
          'automatically. End it from the Tenant Manager tab to stop it immediately.'
      )
    }
    clearSession()
    signOut(auth).catch(err => console.error('[impersonation] sign-out after ending session failed', err))
    // Only the TM-opened tab can be closed by script; when we couldn't notify TM the opener is gone,
    // so leave the tab open (now signed out) so the operator sees the warning.
    if (notifiedTm) window.close()
  }, [session, clearSession])

  if (!session) return null

  const isViewOnly = session.mode === 'view-only'
  const accent = isViewOnly ? '#b45309' : '#b91c1c'
  const bg = isViewOnly ? '#fef3c7' : '#fee2e2'

  return (
    <div role="status" style={{ flexShrink: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '6px 20px',
          background: bg,
          borderBottom: isViewOnly ? 'none' : `1px solid ${accent}`,
          fontSize: 13,
          color: '#1f2937',
        }}
      >
        <svg
          fill="none"
          height="15"
          stroke={accent}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.75"
          viewBox="0 0 24 24"
          width="15"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <span style={{ fontWeight: 600 }}>Impersonating</span>
        <span>{session.tenantName}</span>
        <span style={{ color: '#6b7280' }}>·</span>
        <span
          style={{
            background: isViewOnly ? 'rgba(180,83,9,0.15)' : 'rgba(185,28,28,0.12)',
            color: accent,
            border: `1px solid ${accent}`,
            borderRadius: 4,
            padding: '1px 6px',
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {isViewOnly ? 'View only' : 'Edit mode'}
        </span>
        <button
          style={{
            marginLeft: 'auto',
            background: '#fff',
            border: `1px solid ${accent}`,
            borderRadius: 6,
            color: accent,
            padding: '3px 12px',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
          type="button"
          onClick={handleEndSession}
        >
          End session
        </button>
      </div>
      {isViewOnly && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 20px',
            background: '#fffbeb',
            borderBottom: `1px solid ${accent}`,
            fontSize: 12,
            color: '#92400e',
          }}
        >
          <svg
            fill="none"
            height="14"
            stroke={accent}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            style={{ flexShrink: 0 }}
            viewBox="0 0 24 24"
            width="14"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" x2="12" y1="9" y2="13" />
            <line x1="12" x2="12.01" y1="17" y2="17" />
          </svg>
          <span>
            View-only mode is not yet fully enforced on the server. Avoid creating, editing, or deleting data while impersonating this
            tenant.
          </span>
        </div>
      )}
    </div>
  )
}
